import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Estándar de ícono en botones de acción (aplicado primero en
 * /admin/comunicacion/configuracion-citas, ej. Legal Flow "+ Nueva solicitud"):
 * tanto el botón PRINCIPAL de una acción/formulario (el único submit) como un CTA
 * secundario (una acción puntual junto a otras, ej. "Nueva Cita") usan variant="default"
 * (sólido: fondo verde pleno, texto/ícono casi blanco, hover un poco más claro). La única
 * diferencia entre ambos es el ícono: el principal va SIN ícono decorativo (solo texto; el
 * spinner de loading sí se conserva, es feedback de estado); el CTA SÍ lleva ícono.
 * variant="primary-outline" (fondo blanco, borde sutil y texto verdes) queda reservado
 * como secundario para casos puntuales, no para CTAs normales. Ver ConfiguracionCitas.tsx:
 * "Crear Cita"/"Agregar bloqueo"/"Nueva Cita"/"Guardar"/"Agregar" tipo de cita - todos
 * variant="default", con o sin ícono según corresponda.
 */
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Estándar de portal: botón de descarte (Cancelar/Cerrar). Hover → rojo.
        cancel:
          "border border-input bg-background text-muted-foreground hover:border-destructive/40 hover:text-destructive",
        // Estándar de portal: botón secundario para casos puntuales - borde sutil (verde al
        // 30%, casi imperceptible) y texto verde pleno, fondo blanco; en hover se rellena
        // por completo (verde pleno, texto pasa a blanco) para que el cambio de estado se
        // note. Aplica a los ~28 lugares del portal que usan esta variante.
        "primary-outline":
          "border border-primary/30 bg-background text-primary hover:border-primary hover:bg-primary hover:text-primary-foreground",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Escala del panel (ver escala-panel.ts): acciones secundarias, toolbars y paneles densos.
        // h-8, texto 13px e ícono 14px. Aditivo: los tamaños de arriba no cambian.
        compact: "h-8 rounded-md px-3 text-[13px] [&_svg]:size-3.5",
        "icon-compact": "h-8 w-8 [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
